import classNames from 'classnames';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { handleKeyboardSelect } from '../../helpers/Accessibility';
import { Check } from './Check';
import { StepModel } from './interfaces/Step';
import style from './Step.module.scss';

interface StepProps {
  index: number;
  step: StepModel;
}

const CONFIRM_STEP_INDEX = 4;

const Step: FC<StepProps> = ({
  step: { id, isCompleted, isDisabled: isStepDisabled, disableUserNavigation, uri }, index
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isActive = pathname === uri;
  const isDisabled = isStepDisabled || !!disableUserNavigation;

  const stepContainerClasses = classNames({
    [style.stepContainer]: true,
    [style.stepContainerFullWidth]: index !== CONFIRM_STEP_INDEX,
  });

  const handleClick = useCallback(() => {
    navigate(uri);
  }, [navigate, uri]);

  const handleKeyDown = useMemo(() => handleKeyboardSelect(handleClick), [handleClick]);

  const circleClassnames = classNames({
    [style.circle]: true,
    [style.circleCompleted]: isCompleted,
    [style.circleDisabled]: isDisabled,
    [style.completedBg]: isCompleted,
    [style.circleCurrent]: isActive,
  });

  const buttonClasses = classNames({
    [style.numberWrapper]: true,
    [style.buttonEnabled]: !isDisabled,
    [style.buttonDisabled]: isDisabled,
  });

  const stepNameClasses = classNames({
    [style.stepName]: true,
    [style.labelStepCompleted]: isCompleted,
    [style.stepDisabledFont]: isDisabled,
    [style.stepNameActive]: isActive,
  });

  const lineStyles = classNames({
    [style.line]: true,
    [style.lineBg]: !isCompleted,
    [style.completedBg]: isCompleted,
    [style.circleDisabled]: isDisabled,
  });

  return (
    <div className={stepContainerClasses}>
      <div
        aria-current={isActive ? 'page' : 'false'}
        aria-disabled={isDisabled}
        aria-label={t(`stepsBar:${id}`)}
        className={buttonClasses}
        id={`${id}-button`}
        onClick={isDisabled ? undefined : handleClick}
        onKeyDown={isDisabled ? undefined : handleKeyDown}
        tabIndex={isDisabled ? -1 : 0}
      >
        <div className={circleClassnames}>
          { isActive && <div className={style.selectedCircle}/> }
          {
            isCompleted
              ? <Check/> : (
                <span>{index}</span>
              )
          }
        </div>
        <span className={stepNameClasses}>
          {t(`stepsBar:${id}`)}
          <div className={style.clicker}></div>
        </span>
      </div>
      { index !== CONFIRM_STEP_INDEX ? <div className={lineStyles}/> : null}
    </div>
  );
};

export default Step;
